import { ProctorEventTypes } from './proctorEventTypes';
import {
    InterviewStatuses, IntegrityVerdicts, QuestionKinds, ResponseTypes, SurveyTypes,
    MessageChannels, LinkTypes
} from './statuses';

/**
 * The one place that turns stored codes into words a recruiter can read.
 *
 * Statuses, verdicts and proctor event types are all persisted as short identifiers, which is right
 * for storage and wrong for a screen. Every page that shows one to a person goes through here, so
 * "TabSwitch" and "HighRisk" cannot leak onto a report again, and so the wording only has to be
 * changed once.
 *
 * This lives in the shared package rather than the API because the React report renders the same
 * labels. Two copies would drift, which is the exact failure this file exists to prevent.
 */
export interface SignalCopy {
    label: string;
    explanation: string;
    threshold?: boolean;
    sustained?: boolean;
}

const SIGNALS: Record<string, SignalCopy> = {
    [ProctorEventTypes.TabSwitch]: {
        label: 'Switched tab or minimised',
        explanation: 'The interview page was hidden — the candidate moved to another tab or app.'
    },
    [ProctorEventTypes.WindowBlur]: {
        label: 'Window lost focus',
        explanation:
            'Focus moved to another window while the page stayed visible. Noisier than a tab switch.'
    },
    [ProctorEventTypes.FullscreenExit]: {
        label: 'Left fullscreen',
        explanation: 'Only fires when fullscreen is required below.'
    },
    [ProctorEventTypes.Paste]: {
        label: 'Pasted content',
        explanation: 'Content was pasted into the page, suggesting a prepared answer.'
    },
    [ProctorEventTypes.ContextMenu]: {
        label: 'Opened the right-click menu',
        explanation: 'Weak signal on its own. Recorded, and by default costs nothing.'
    },
    [ProctorEventTypes.UnloadAttempt]: {
        label: 'Tried to leave the page',
        explanation: 'The candidate attempted to close or navigate away mid-interview.'
    },
    [ProctorEventTypes.AwayTooLong]: {
        label: 'Away too long — submitted automatically',
        explanation:
            'The candidate left the page or fullscreen and did not return within the limit set '
            + 'below, so the interview was submitted with the answers given up to that point. '
            + 'Nothing was discarded.'
    },
    [ProctorEventTypes.NoFace]: {
        label: 'No face in frame',
        explanation: 'No face was detected for the sustained period below.',
        sustained: true
    },
    [ProctorEventTypes.MultipleFaces]: {
        label: 'More than one face',
        explanation:
            'A second person appeared in frame. The strongest single signal of assistance.',
        sustained: true
    },
    [ProctorEventTypes.LookingAway]: {
        label: 'Looking away from the screen',
        explanation: 'Head turned beyond the tolerance below for the sustained period.',
        threshold: true,
        sustained: true
    },
    [ProctorEventTypes.CameraLost]: {
        label: 'Camera stopped working',
        explanation:
            'The camera was switched off, unplugged, or stopped sending a picture. Different from ' +
            'no face in frame, where the camera is working and nobody is in front of it.',
        sustained: true
    },
    [ProctorEventTypes.MultipleScreens]: {
        label: 'More than one screen',
        explanation:
            'A second display was attached. This does not detect screen sharing — no browser can — ' +
            'but a second screen is somewhere answers could be read from.'
    },
    [ProctorEventTypes.ScreenshotAttempt]: {
        label: 'Tried to take a screenshot',
        explanation:
            'The Print Screen key was pressed. This records an attempt and cannot prevent one — the ' +
            "operating system's own snipping tools never reach the page, and a phone camera defeats " +
            'any software check.'
    },
    [ProctorEventTypes.BlockedKeyInput]: {
        label: 'Pressed a key while the keyboard was locked',
        explanation:
            'Answers are spoken, so the keyboard is disabled during the assessment. The press had no ' +
            'effect and by default costs nothing — a run of them is what to look at.'
    },
    [ProctorEventTypes.ClientSilence]: {
        label: 'The browser went quiet',
        explanation:
            'No proctoring data arrived for an unusually long stretch, which points to the page being ' +
            'paused, the script stopped, or the network cut. Worked out by the server after the ' +
            "interview, so a candidate's browser cannot suppress it."
    },
    [ProctorEventTypes.ClientTamperSuspected]: {
        label: 'Client tampering suspected',
        explanation:
            'Raised by the server when a snapshot contradicts what the browser reported. Cannot be ' +
            "triggered by the candidate's browser."
    },
    [ProctorEventTypes.PeriodicSample]: {
        label: 'Routine check',
        explanation: 'A regular sample of the camera. Never counts against the candidate.'
    },
    [ProctorEventTypes.Baseline]: {
        label: 'Reference photo',
        explanation: 'Captured at the start, to compare later frames against.'
    }
};

// Lower-cased index, so lookups match the C# StringComparer.OrdinalIgnoreCase dictionary.
const SIGNALS_BY_LOWER = new Map(
    Object.entries(SIGNALS).map(([key, copy]) => [key.toLowerCase(), copy])
);

/** Full copy for a signal, or null when the stored type is not one we know. */
export const signal = (eventType: string | null | undefined): SignalCopy | null =>
    (eventType ? SIGNALS_BY_LOWER.get(eventType.toLowerCase()) : undefined) ?? null;

/**
 * Label for a signal, falling back to the raw stored value. A code on screen is bad; a blank where
 * evidence should be is worse, so an unknown type still shows something.
 */
export const signalLabel = (eventType: string): string => signal(eventType)?.label ?? eventType;

/** How far through an interview is. Matches the wording on the list page. */
export function statusLabel(status: string): string {
    switch (status) {
        case InterviewStatuses.Pending: return 'Not started';
        case InterviewStatuses.InProgress: return 'Being taken';
        case InterviewStatuses.Processing: return 'Being processed';
        case InterviewStatuses.Completed: return 'Completed';
        case InterviewStatuses.Cancelled: return 'Cancelled';
        case InterviewStatuses.Expired: return 'Link ran out';
        default: return status;
    }
}

/**
 * The three question-bank sections, and the two kinds of employee interaction. "Listening session"
 * rather than "Listening" because the bare word reads as an instruction on a button.
 */
export function kindLabel(kind: string): string {
    switch (kind) {
        case QuestionKinds.Interview: return 'Interview';
        case QuestionKinds.Survey: return 'Survey';
        case QuestionKinds.Listening: return 'Listening session';
        default: return kind;
    }
}

/** How a question is answered, said the way the admin picking it thinks about it. */
export function responseTypeLabel(responseType: string): string {
    switch (responseType) {
        case ResponseTypes.Text: return 'Typed answer';
        case ResponseTypes.Checklist: return 'Choose from a list';
        case ResponseTypes.Audio: return 'Spoken answer';
        case ResponseTypes.Video: return 'Answer on camera';
        default: return responseType;
    }
}

/**
 * "Questionnaire" rather than "Form", because a form is what the engineer sees and a questionnaire
 * is what the person filling it in sees.
 */
export function surveyTypeLabel(surveyType: string): string {
    switch (surveyType) {
        case SurveyTypes.Form: return 'Questionnaire';
        case SurveyTypes.General: return 'Spoken conversation';
        default: return surveyType;
    }
}

export function verdictLabel(verdict: string): string {
    switch (verdict) {
        case IntegrityVerdicts.Clean: return 'Clean';
        case IntegrityVerdicts.Minor: return 'Minor concerns';
        case IntegrityVerdicts.Suspicious: return 'Suspicious';
        case IntegrityVerdicts.HighRisk: return 'High risk';
        default: return verdict;
    }
}

/**
 * Severity as a word. The report shows severity as a coloured chip, which says nothing to anyone
 * who cannot pick the colour out — on a projector, in greyscale, or colour-blind.
 */
export function severityLabel(severity: number): string {
    switch (severity) {
        case 0: return 'Note only';
        case 1: return 'Low';
        case 2: return 'Medium';
        case 3: return 'High';
        default: return `Level ${severity}`;
    }
}

/**
 * "Text message" rather than "SMS", and "WhatsApp" with its own capitals. The stored codes are ours;
 * these are what the three channels are called by the people choosing between them.
 */
export function channelLabel(channel: string): string {
    switch (channel) {
        case MessageChannels.Email: return 'Email';
        case MessageChannels.Whatsapp: return 'WhatsApp';
        case MessageChannels.Sms: return 'Text message';
        default: return channel;
    }
}

/** What the recipient column holds for each channel, for a form label or an error. */
export function channelAddressLabel(channel: string): string {
    return channel === MessageChannels.Email ? 'email address' : 'mobile number';
}

export function linkTypeLabel(linkType: string): string {
    switch (linkType) {
        case LinkTypes.Employee: return 'Personal link';
        case LinkTypes.Shared: return 'Shared link';
        default: return linkType;
    }
}
